import { Paper, Popover, useMantineTheme } from '@mantine/core'
import { noop } from 'lodash'
import moment, { Moment } from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { CSSProperties } from 'react'
import { Button as BsButton } from 'react-bootstrap'

import TransitionMantineTooltip from '../TransitionMantineTooltip'
import Button from './Button'
import {
  parseDate,
  TParseDate,
  TParseDateObject,
  TValueDatePicker,
} from './helpers'
import Panel from './Panel'

export type TStringMoment = string | Moment
export type TDatePicker = {
  value?: TValueDatePicker
  onChange?: (data: TValueDatePicker) => void
  /** Show range picker with start date and end date */
  isRange?: boolean
  /** Position of the popover that container the calendar */
  placement?:
    | 'top left'
    | 'top right'
    | 'bottom right'
    | 'bottom left'
    | 'right center'
    | 'left center'
    | 'top center'
    | 'bottom center'
  /** Create block level button that span the full width of a parent */
  block?: boolean
  /** Display the wrapper as inline element */
  inline?: boolean
  /** Show timepicker */
  timepicker?: boolean
  /** Show timepicker */
  timePicker?: boolean
  /** Minimum selectable value */
  min?: TStringMoment | undefined | null
  /** Minimum selectable value */
  minDate?: TStringMoment | undefined | null
  /** Maximum selectable value */
  max?: TStringMoment | undefined | null
  /** Maximum selectable value */
  maxDate?: TStringMoment | undefined | null
  /** Name of the picker when use in form */
  name?: TStringMoment
  /** CSS class name */
  className?: string
  buttonStyles?: CSSProperties
  customTrigger?: React.ReactElement
  disabled?: boolean
  validateTime?: boolean
  tooltip?: React.ReactNode
  flex?: boolean
  customWidth?: string
  id?: string
  autoclose?: boolean
  nodisabled?: boolean
  clearable?: boolean
  placeholder?: string
  onOpenChange?: (isOpen: boolean) => void
  enforceFullDay?: boolean
}

const DatePicker = (props: TDatePicker): React.ReactElement => {
  const {
    value,
    onChange = noop,
    isRange,
    placement,
    block,
    inline,
    timepicker,
    timePicker,
    min,
    minDate,
    max,
    maxDate,
    name,
    className,
    buttonStyles = {},
    customTrigger = undefined,
    disabled = false,
    validateTime,
    tooltip,
    flex = false,
    customWidth,
    placeholder,
    onOpenChange = noop,
    enforceFullDay = false,
  } = props

  const [isOpen, setIsOpen] = useState(false)

  const open = (): void => {
    setIsOpen(true)
  }

  useEffect(() => {
    onOpenChange(isOpen)
  }, [isOpen])

  const close = (): void => setIsOpen(false)

  const change = (data: TParseDate): void => {
    let newValue: TValueDatePicker
    if (!isRange) {
      newValue = moment(data as Moment).toISOString()
    } else {
      let startDate = moment((data as TParseDateObject).from)
      let endDate = moment((data as TParseDateObject).until)
      if (enforceFullDay) {
        startDate = startDate.startOf('day')
        endDate = endDate.endOf('day')
      }
      newValue = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      }
    }

    onChange(newValue)
  }

  const parsedDate = useMemo(() => parseDate(value), [value])

  // TODO: re-add tooltip for button

  const theme = useMantineTheme()

  return (
    <div
      style={{
        display: inline ? 'inline' : flex ? 'flex' : 'block',
        width: customWidth || '',
      }}
    >
      <Popover
        opened={isOpen}
        onClose={() => close()}
        withinPortal
        zIndex={99999}
      >
        <Popover.Target>
          {customTrigger ? (
            React.cloneElement(customTrigger, { onClick: open })
          ) : (
            <BsButton
              variant="light"
              onClick={() => setIsOpen(!isOpen)}
              id={props.id}
              block={block}
              cy-name={name}
              className={className}
              disabled={disabled}
              style={{
                ...buttonStyles,
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              <Button
                value={value}
                isRange={isRange as boolean}
                timepicker={(timepicker || timePicker) as boolean}
                placeholder={placeholder}
              />
            </BsButton>
          )}
        </Popover.Target>

        <Popover.Dropdown style={{ boxShadow: theme.shadows.md }}>
          <Panel
            {...props}
            onClose={close}
            onChange={change}
            value={parsedDate as TParseDate}
            timepicker={timepicker || timePicker}
            min={min || minDate}
            max={max || maxDate}
            validateTime={validateTime}
          />
        </Popover.Dropdown>
      </Popover>
      {/* <Popup
        flowing
        popperModifiers={[
          {
            name: 'preventOverflow',
            enabled: true,
          },
        ]}

        on="click"
        content={
          <Panel
            {...props}
            onClose={close}
            onChange={change}
            value={parsedDate as TParseDate}
            timepicker={timepicker || timePicker}
            min={min || minDate}
            max={max || maxDate}
            validateTime={validateTime}
          />
        }
      /> */}
    </div>
  )
}

export { default as Preview } from './Preview'

export default DatePicker

export { parseDate }
